import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { initializePhraseAppEditor } from 'ngx-translate-phraseapp';
import { catchError, switchMap, take, tap } from 'rxjs/operators';
import { AuthService, User } from './auth';
import { Observable } from 'rxjs';
import { PrimeNGConfig } from 'primeng/api';
import { NGXLogger } from 'ngx-logger';

// These are the translations we have (manually added and via phrase)
// If we have more languages (e.g. via phrase getting greek language file) then add it here
// and add the mapping from the language code to the file
enum SCIP_LANGUAGE_FILES {
  DE = 'de',
  EN = 'en',
  ES = 'es',
  FR = 'fr',
  JA = 'ja',
  ZH = 'zh'
}

// This is the list of supported language keys. Coming e.g. from the auth-server or from the browser language.
// There are libraries which would be able to convert between all language keys but they are way to huge for
// our application which currently only supports 6 languages.
enum SCIP_SUPPORTED_LANGUAGES_KEYS {
  DE = 'de',
  DEU = 'deu',
  EN = 'en',
  ENG = 'eng',
  ES = 'es',
  SPA = 'spa',
  FR = 'fr',
  FRA = 'fra',
  FRE = 'fre',
  JA = 'ja',
  JPN = 'jpn',
  ZH = 'zh',
  ZHO = 'zho',
  CHI = 'chi'
}

@Injectable({
  providedIn: 'root'
})
export class InitTranslationService {
  private readonly DEFAULT_LANGUAGE_FILE: string = SCIP_LANGUAGE_FILES.EN;

  // This is the mapping between the language keys (coming from the outside) to our translation files.
  private languageMapping: Map<string, string> = new Map([
    [SCIP_SUPPORTED_LANGUAGES_KEYS.DE, SCIP_LANGUAGE_FILES.DE],
    [SCIP_SUPPORTED_LANGUAGES_KEYS.DEU, SCIP_LANGUAGE_FILES.DE],
    [SCIP_SUPPORTED_LANGUAGES_KEYS.EN, SCIP_LANGUAGE_FILES.EN],
    [SCIP_SUPPORTED_LANGUAGES_KEYS.ENG, SCIP_LANGUAGE_FILES.EN],
    [SCIP_SUPPORTED_LANGUAGES_KEYS.ES, SCIP_LANGUAGE_FILES.ES],
    [SCIP_SUPPORTED_LANGUAGES_KEYS.SPA, SCIP_LANGUAGE_FILES.ES],
    [SCIP_SUPPORTED_LANGUAGES_KEYS.FR, SCIP_LANGUAGE_FILES.FR],
    [SCIP_SUPPORTED_LANGUAGES_KEYS.FRA, SCIP_LANGUAGE_FILES.FR],
    [SCIP_SUPPORTED_LANGUAGES_KEYS.FRE, SCIP_LANGUAGE_FILES.FR],
    [SCIP_SUPPORTED_LANGUAGES_KEYS.JA, SCIP_LANGUAGE_FILES.JA],
    [SCIP_SUPPORTED_LANGUAGES_KEYS.JPN, SCIP_LANGUAGE_FILES.JA],
    [SCIP_SUPPORTED_LANGUAGES_KEYS.ZH, SCIP_LANGUAGE_FILES.ZH],
    [SCIP_SUPPORTED_LANGUAGES_KEYS.ZHO, SCIP_LANGUAGE_FILES.ZH],
    [SCIP_SUPPORTED_LANGUAGES_KEYS.CHI, SCIP_LANGUAGE_FILES.ZH]
  ]);

  constructor(private translate: TranslateService, private authService: AuthService, private primeNGConfig: PrimeNGConfig, private logger: NGXLogger) {}

  initTranslationService(): Observable<any> {
    this.translate.setDefaultLang(this.DEFAULT_LANGUAGE_FILE);
    this.initializePhrase();

    return this.authService.getUser$().pipe(
      take(1),
      switchMap((currentUser: User) => {
        return this.handleLanguageCode(currentUser.language);
      })
    );
  }

  private initializePhrase(): void {
    const config = {
      projectId: '24d409d5aaf7784125fe0510cdb70eab',
      phraseEnabled: false,
      prefix: '{{__',
      suffix: '__}}',
      fullReparse: true
    };
    initializePhraseAppEditor(config);
  }

  private handleLanguageCode(usersLanguage: string): Observable<any> {
    let languageFileToUse = this.getLanguageFile(usersLanguage);

    // If its not set now, the user language was not mappable, fallback to browser language
    if (!languageFileToUse) {
      this.logger.error(`TranslationInitService: Could not map user-language: "${usersLanguage}" to a supported language file`);
      languageFileToUse = this.getLanguageFile(this.translate.getBrowserLang());
    }

    // If its not set now, the browser language was not mappable too, fallback to default hardcoded language
    if (!languageFileToUse) {
      this.logger.error(`TranslationInitService: Could not map browser-language: "${this.translate.getBrowserLang()}" to a supported language file`);
      languageFileToUse = this.DEFAULT_LANGUAGE_FILE;
    }

    // We need to manually catch errors as otherwise they would bubble and break the translation service partially
    // (see https://github.com/ngx-translate/core/issues/1280)
    return this.translate.use(languageFileToUse).pipe(
      catchError(() => {
        // That catchError happens, when we could map the code to a file, but the file could not be loaded from the backend
        this.logger.error('TranslationInitService, please report that the loading of the language did not worked for', languageFileToUse);
        return this.translate.use(this.DEFAULT_LANGUAGE_FILE);
      }),
      take(1),
      tap(() => {
        //Set the translation for primeng components
        this.primeNGConfig.setTranslation(this.translate.instant('primeng'));
      })
    );
  }

  private getLanguageFile(languageCode: string = ''): string {
    const convertedLanguageCode = languageCode.toLowerCase();
    // Lets check if we know the external language code and if yes use our mapped language for that code
    if (convertedLanguageCode.length && this.languageMapping.has(convertedLanguageCode)) {
      return this.languageMapping.get(convertedLanguageCode);
    } else {
      return undefined; // We do not have a mapping for the given languageCode
    }
  }
}
